import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import type { TableCellNode } from "@lexical/table";
import PaletteOutlined from "@mui/icons-material/PaletteOutlined";
import { type FC, useCallback, useEffect, useState } from "react";
import type { PopoverController } from "../../../../../../utils/hooks/use-popover";
import { ColourPicker } from "../../../../../generic/components/colour-picker/colour-picker";
import type { DataTableCellNode } from "../../../nodes/data-table-cell-node";
import type { StylableTableCellMui } from "../../../nodes/stylable-table-cell-mui";
import { ToolbarIconButton } from "../../toolbar/toolbar-icon-button";

export const BackgroundColourButton: FC<{
  tableCellNode: StylableTableCellMui | TableCellNode | DataTableCellNode;
  popoverInputRef: React.RefObject<HTMLInputElement | null>;
  popover: PopoverController<HTMLButtonElement>;
}> = ({ tableCellNode, popoverInputRef, popover }) => {
  const [editor] = useLexicalComposerContext();
  const [colour, setTempColour] = useState<string>("");

  useEffect(() => {
    editor.read(() => {
      const cellBackgroundColor = tableCellNode.getBackgroundColor() ?? "";
      if (cellBackgroundColor !== colour) {
        setTempColour(cellBackgroundColor);
      }
    });
  }, [editor, tableCellNode, colour]);

  const setColour = useCallback(
    (c: string, skipAddingToHistoryStack?: boolean) => {
      editor.update(
        () => tableCellNode.setBackgroundColor(c),
        skipAddingToHistoryStack ? { tag: "historic" } : {},
      );
      setTempColour(c || "");
    },
    [editor, tableCellNode],
  );

  return (
    <>
      <ToolbarIconButton
        data-analytics-id="lexical-table-menu-cell-background-colour"
        disabled={!editor._editable}
        label={"Background color"}
        icon={<PaletteOutlined fontSize="small" />}
        data-testid="table-menu-colour"
        aria-label="change-cell-background-colour"
        onClick={popover.handleToggle}
      />
      <ColourPicker
        popover={popover}
        colour={colour}
        onInput={setColour}
        onChange={setColour}
        inputRef={popoverInputRef}
      />
    </>
  );
};
